import type { ImagePickerResponse } from 'react-native-image-picker';
import type { ActionSheetOptions } from '@expo/react-native-action-sheet';
import { Result } from 'neverthrow';

import type { Asset, DropzoneFile } from '@rover/types/src/ImagePicker';

import type { useActionSheet } from '../../utils/utils';

import MediaUploadError from './Upload/MediaUploadError';
import UploadTask, {
  AttachableUploadTask,
  CancelledUploadTask,
  FailedUploadTask,
  ProgressUploadTask,
  SuccessfulUploadTask,
} from './Upload/UploadTask';
import {
  IMAGE_ACCEPT_STRING,
  IMAGE_AND_VIDEO_ACCEPT_STRING,
  VIDEO_ACCEPT_STRING,
} from './constants';
import MediaFile from './MediaFile';
import MediaSelectionError from './MediaSelectionError';

// reexports
export type { ProgressUploadTask, SuccessfulUploadTask };

/**
 * What the picker reported for a file. Only native reports these. `original` is
 * set when the MediaFile was resized, and describes the file before the resize.
 */
export interface MediaDimensions {
  width?: number;
  height?: number;
  durationSeconds?: number;
  original?: { name: string; file: Asset; size?: number };
}

/** The kinds of media that RSDK Media handles */
export enum MediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
}

/** Options for selecting media */
export interface SelectMediaOptions {
  // default to single selection
  multiple?: boolean;
  /** If undefined then images and video are allowed */
  type?: MediaType;
  /** Callback for successful media selection */
  onSelectionSuccess?: (mediaFiles: MediaFile[]) => void;
  // provide a custom implementation for `useActionSheet`. Mostly for testing purpose
  useActionSheet?: typeof useActionSheet;
}

/** ActionSheet related types */
export type ShowActionSheetWithOptions = (
  options: ActionSheetOptions
) => Promise<number | undefined>;
export type ImagePickerResizeResult = Result<ImagePickerResponse, MediaSelectionError>;

/** Possible accept strings for the Media Selection input */
export type FileInputAcceptStrings =
  | typeof IMAGE_ACCEPT_STRING
  | typeof VIDEO_ACCEPT_STRING
  | typeof IMAGE_AND_VIDEO_ACCEPT_STRING;

export type SelectMediaResult = Result<MediaFile[], MediaSelectionError>;

/** API returned by useSelectMedia hook */
export interface SelectMediaHookAPI {
  /** Error that happened during media selection */
  error?: MediaSelectionError;
  /** Props to be passed to the input element */
  inputProps: {
    type: 'file';
    accept: FileInputAcceptStrings;
    multiple?: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  };
  /** Props to be passed to a react-dropzone */
  dropzoneProps: {
    onDrop: (acceptedFiles: DropzoneFile[], rejectedFiles: DropzoneFile[]) => Promise<void>;
  };
  /** Array of selected MediaFiles */
  selectedMedia: MediaFile[];
  /** onChange handler for RSDK Media selection */
  selectMedia: () => void;
}

// Options describing the destination/use of media to be uploaded
// see https://github.com/roverdotcom/specs/blob/master/api/images.md#optional-fields
// for more details on these upload options
export interface ContentUrl {
  // The content object URL.
  // Example: https://www.rover.com/api/v7/service-deliveries/N8rKnnmQ
  contentUrl: string;
}
export interface ResourceOPK {
  objectOPK?: string;
  objectType:
    | 'pets'
    | 'service-deliveries'
    | 'people'
    | 'conversations'
    | 'messages'
    | 'cat-provider-profile';
}
export interface AttachMediaOptions {
  // The URL for the content object, required when attaching
  // Example: https://www.rover.com/api/v7/service-deliveries/N8rKnnmQ/images
  // Example: https://www.rover.com/api/v7/conversations/N8rKnnmQ/videos
  contentObjectMediaUrl: string;
  // An optional caption for uploaded images only
  caption?: string;
}

/** image uploads require one of these for proper auth & upload */
export type UploadImageOptions = ContentUrl | ResourceOPK;

/** UploadImageOptions is optional here because videos do not require them */
export type UploadMediaOptions = Partial<UploadImageOptions> & {
  attach?: AttachMediaOptions;
  persistenceKey?: string;
  timesToAutoRetry?: number;
  secondsBetweenAutoRetries?: number;
};

export type UploadTaskConfig = {
  file: MediaFile;
  opts?: UploadMediaOptions;
};

/** The result of a successful media upload */
export interface UploadedMedia {
  url: string;
}

export type UploadMediaResult = Result<UploadedMedia, MediaUploadError>;

export type UploadTaskStatus = 'queued' | 'in progress' | 'complete' | 'errored' | 'cancelled';

export type UploadTaskRecord = Record<string, UploadTask>;

export interface UploadTaskListeners {
  onProgress?: (task: UploadTask) => void;
  onRetry?: (task: UploadTask) => void;
  onFinish?: (task: UploadTask) => void;
  onCancel?: (task: UploadTask) => void;
}

export type UploadMediaImperativeAPI = (
  tasks: UploadTaskConfig[],
  listeners?: UploadTaskListeners
) => UploadTaskRecord;

export interface UploadMediaCallbacks {
  onUploadSuccess?: (task: SuccessfulUploadTask) => void;
  onUploadProgress?: (task: ProgressUploadTask) => void;
  onUploadFailed?: (task: FailedUploadTask) => void;
  onAllFinished?: () => void;
  onUploadCancelled?: (task: CancelledUploadTask) => void;
}

type UseUploadMediaOpts = {
  persistenceKey?: string;
};

/** API returned by useUploadMedia hook */
export interface UploadMediaHookAPI {
  error?: MediaUploadError;
  uploadTasks: UploadTaskRecord;
  uploadMedia: (tasks: UploadTaskConfig[], callbacks?: UploadMediaCallbacks) => void;
  retryUploadTasks: (tasks: FailedUploadTask[], callbacks?: UploadMediaCallbacks) => void;
  removeUploadTask: (taskId: string) => void;
  queuedAndInProgressTasks: ProgressUploadTask[];
  successfulUploadTasks: SuccessfulUploadTask[];
  failedUploadTasks: FailedUploadTask[];
  isUploading: boolean;
}

/** Options for the useSelectAndUploadMedia combo hook */
export interface SelectAndUploadMediaOptions extends UseUploadMediaOpts {
  /** onSelectionSuccess is provided by the hook */
  selectOptions: Omit<SelectMediaOptions, 'onSelectionSuccess'>;
  uploadOptions?: {
    attach?: AttachMediaOptions;
  };
  /** extra options to attach to image upload tasks */
  imageUploadOptions?: UploadImageOptions;
  uploadListeners?: UploadMediaCallbacks;
}

/** API returned by the useSelectAndUploadMedia combo hook */
export interface SelectAndUploadMediaHookAPI {
  dropzoneProps: SelectMediaHookAPI['dropzoneProps'];
  error?: MediaSelectionError | MediaUploadError;
  inputProps: SelectMediaHookAPI['inputProps'];
  isUploading: UploadMediaHookAPI['isUploading'];
  selectMedia: SelectMediaHookAPI['selectMedia'];
  successfulUploadTasks: UploadMediaHookAPI['successfulUploadTasks'];
  uploadTasks: UploadMediaHookAPI['uploadTasks'];
  failedUploadTasks: UploadMediaHookAPI['failedUploadTasks'];
  queuedAndInProgressTasks: UploadMediaHookAPI['queuedAndInProgressTasks'];
  retryUploadTasks: UploadMediaHookAPI['retryUploadTasks'];
  removeUploadTask: UploadMediaHookAPI['removeUploadTask'];
}

/** API returned by attachMedia */
export type AttachMediaImperativeAPI = (
  task: AttachableUploadTask
) => Promise<Result<true, MediaUploadError>>;

/** Rover SDK module for handling media selection and upload */
export interface RSDKMedia {
  /** hook interface for using selectMedia */
  useSelectMedia: (opts?: SelectMediaOptions) => SelectMediaHookAPI;
  /** hook interface for using uploadMedia */
  useUploadMedia: (opts?: UseUploadMediaOpts) => UploadMediaHookAPI;
  /** starts the upload process for MediaFiles */
  uploadMedia: UploadMediaImperativeAPI;
  /** combo hook for immediately uploading selected MediaFiles */
  useSelectAndUploadMedia: (opts: SelectAndUploadMediaOptions) => SelectAndUploadMediaHookAPI;
  /** starts the attach to content object process */
  attachMedia: AttachMediaImperativeAPI;
}
